import { computed, onBeforeUnmount, ref } from 'vue'

// Mirrors stripModelArtifacts in agents/conversation-history.js, which is CommonJS
// and so cannot be imported here. Keep the two in sync.
const MODEL_ARTIFACT = /<\|[a-z_]*\|?>|<[a-z_]+\|>/i

function stripModelArtifacts(text) {
  if (typeof text !== 'string') return ''

  const match = MODEL_ARTIFACT.exec(text)

  return (match ? text.slice(0, match.index) : text).trimEnd()
}

function createConversationId() {
  return crypto.randomUUID()
}

function parseToolArgs(value) {
  if (value == null || value === '') return {}
  if (typeof value === 'object' && !Array.isArray(value)) return value

  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function normalizeAssistantContent(content) {
  if (typeof content === 'string') return content

  if (Array.isArray(content)) {
    return content
      .map((part) => {
        if (typeof part === 'string') return part
        if (typeof part?.text === 'string') return part.text
        if (typeof part?.content === 'string') return part.content
        return ''
      })
      .join('')
  }

  return ''
}

function createToolCallSnapshot(message) {
  return {
    name: message.name ?? message.toolName ?? 'tool',
    args: parseToolArgs(message.arguments ?? message.args),
    status: 'running',
    toolCallId: message.call_id ?? message.callId ?? message.id ?? null,
  }
}

function isToolCallMessage(message) {
  return Boolean(message?.type && /(?:_call|function_call)$/.test(message.type) && !/(?:_result|_output)$/.test(message.type))
}

function isToolResultMessage(message) {
  return Boolean(message?.type && /(?:_result|_output)$/.test(message.type))
}

function normalizeMessage(message) {
  if (!message || typeof message !== 'object') return null

  if (message.role === 'tool_call') {
    return {
      role: 'tool_call',
      name: message.name ?? 'tool',
      args: message.args ?? {},
      result: message.result ?? null,
      status: message.status ?? 'done',
      toolCallId: message.toolCallId ?? null,
    }
  }

  if (message.role === 'user') {
    return {
      role: 'user',
      content: normalizeAssistantContent(message.content),
    }
  }

  if (message.role === 'assistant' || (message.type === 'message' && message.role === 'assistant')) {
    return {
      role: 'assistant',
      content: normalizeAssistantContent(message.content),
    }
  }

  return null
}

// Interactive prompts emit { text, display } so the model gets a self-contained
// answer; the plain chat input and older prompts still emit a bare string.
export function normalizeAnswer(answer) {
  if (typeof answer === 'string') {
    const text = answer.trim()
    return { text, display: text }
  }

  const text = typeof answer?.text === 'string' ? answer.text.trim() : ''
  const display = typeof answer?.display === 'string' ? answer.display.trim() : ''

  return { text, display: display || text }
}

export default function useAgentConversations() {
  const conversationId = ref(createConversationId())
  const messages = ref([])
  const streaming = ref(false)
  // Deltas arrive in fragments, so a control token can straddle two of them. The
  // buffer stays raw and is only cleaned for display; stripping it in place would
  // drop the marker and then append whatever followed it.
  const streamBufferRaw = ref('')
  const streamBuffer = computed(() => stripModelArtifacts(streamBufferRaw.value))
  const activeTool = ref(null)
  const pendingQuestion = ref(null)
  const pendingBoost = ref(null)
  const pendingToolPrompt = ref(null)
  const conversations = ref([])
  const activeConversationId = ref(null)
  const saveToHistory = ref(true)
  const historyLoading = ref(false)
  const historyError = ref('')
  const deletingConversationIds = ref([])
  const eventHandlers = new Set()
  // Set when a send fails (network error or an `error` SSE event) so the UI can
  // offer a "Try again" action; cleared at the start of every new attempt.
  const lastFailedSend = ref(null)

  let activeRequestController = null
  let currentAttempt = null
  const pendingToolCalls = new Map()

  function notifyEvent(event) {
    for (const handler of eventHandlers) {
      try {
        void handler(event)
      } catch (error) {
        console.error('Error in conversation event handler:', error)
      }
    }
  }

  function onEvent(handler) {
    eventHandlers.add(handler)

    return () => {
      eventHandlers.delete(handler)
    }
  }

  // Shared by the request_boost SSE event and the /boost slash command: both just
  // need to show the picker, whether the model asked for it or the user jumped
  // straight to it. `payload` mirrors request_boost's tool args (prompt,
  // field_lens_id, field_lens_name, field_lens_type, allowed_types), all optional.
  function openBoostPicker(payload = {}) {
    pendingBoost.value = payload ?? {}
  }

  // Shared by every slash command whose lookup needs a fixed set of choices
  // rather than plain free text (/winloss, /compat, /fieldlens): each just
  // needs to show its structured form, keyed by the tool config in
  // toolPrompts.js. See buildSlashCommands in useMentionAutocomplete.js for
  // which commands use this vs. a plain `template`.
  function openToolPrompt(key, payload = {}) {
    pendingToolPrompt.value = { key, ...(payload ?? {}) }
  }

  function resetConversationState() {
    messages.value = []
    streamBufferRaw.value = ''
    activeTool.value = null
    pendingQuestion.value = null
    pendingBoost.value = null
    pendingToolPrompt.value = null
    pendingToolCalls.clear()
  }

  function pushAssistantMessage(content) {
    const text = normalizeAssistantContent(content)
    if (!text) return

    messages.value.push({ role: 'assistant', content: text })
  }

  function flushAssistantBuffer(content = streamBuffer.value) {
    const text = normalizeAssistantContent(content)
    if (!text) {
      streamBufferRaw.value = ''
      return
    }

    messages.value.push({ role: 'assistant', content: text })
    streamBufferRaw.value = ''
  }

  function finishStreaming() {
    flushAssistantBuffer()
    activeTool.value = null
    pendingToolCalls.clear()
    streaming.value = false
  }

  function findPendingToolCallIndex(toolCallId) {
    if (!toolCallId) return -1

    return messages.value.findIndex((message) => {
      return message.role === 'tool_call' && message.toolCallId === toolCallId && message.status !== 'done'
    })
  }

  // Called when the user cancels mid-stream: any tool call still awaiting its
  // result would otherwise be stuck showing a spinner forever, so mark it done.
  function cancelPendingToolCalls() {
    for (const toolCallId of pendingToolCalls.keys()) {
      const index = findPendingToolCallIndex(toolCallId)
      if (index === -1) continue

      messages.value.splice(index, 1, { ...messages.value[index], status: 'done', result: 'Cancelled' })
    }
  }

  function applyToolCall(message) {
    flushAssistantBuffer()

    const snapshot = createToolCallSnapshot(message)
    const pendingMessage = {
      role: 'tool_call',
      name: snapshot.name,
      args: snapshot.args,
      result: null,
      status: 'pending',
      toolCallId: snapshot.toolCallId,
    }

    messages.value.push(pendingMessage)

    if (snapshot.toolCallId) {
      pendingToolCalls.set(snapshot.toolCallId, snapshot)
    }
  }

  function applyToolResult(message) {
    const toolCallId = message.call_id ?? message.callId ?? null
    const snapshot = (toolCallId && pendingToolCalls.get(toolCallId)) ?? activeTool.value
    const pendingIndex = findPendingToolCallIndex(toolCallId)
    const completedMessage = {
      role: 'tool_call',
      name: snapshot?.name ?? message.name ?? 'tool',
      args: snapshot?.args ?? {},
      result: message.output ?? message.result ?? null,
      status: 'done',
      toolCallId: toolCallId ?? snapshot?.toolCallId ?? null,
    }

    if (toolCallId) {
      pendingToolCalls.delete(toolCallId)
    }

    if (pendingIndex === -1) {
      messages.value.push(completedMessage)
      return
    }

    messages.value.splice(pendingIndex, 1, completedMessage)
  }

  function applyAssistantMessage(message) {
    const content = normalizeAssistantContent(message.content)

    if (!content && !streamBuffer.value) return

    // The completed message is authoritative: the server has already cleaned it,
    // so prefer it over whatever the deltas built up rather than keeping both.
    if (streamBuffer.value && content) {
      streamBufferRaw.value = ''
      pushAssistantMessage(content)
      return
    }

    flushAssistantBuffer()
    pushAssistantMessage(content)
  }

  function applyConversationMessage(message) {
    const normalizedMessage = normalizeMessage(message)

    if (normalizedMessage) {
      messages.value.push(normalizedMessage)
      return
    }

    if (isToolCallMessage(message)) {
      applyToolCall(message)
      return
    }

    if (isToolResultMessage(message)) {
      applyToolResult(message)
    }
  }

  function hydrateMessages(rawMessages) {
    resetConversationState()

    for (const message of rawMessages ?? []) {
      applyConversationMessage(message)
    }

    activeTool.value = null
    pendingToolCalls.clear()
  }

  function isDeletingConversation(id) {
    return deletingConversationIds.value.includes(id)
  }

  async function loadConversations() {
    historyLoading.value = true
    historyError.value = ''

    try {
      const response = await fetch('/api/v2/conversations')
      if (!response.ok) throw new Error('Failed to load conversations')

      const data = await response.json()
      conversations.value = Array.isArray(data) ? data : []
    } catch (error) {
      conversations.value = []
      historyError.value = error.message || 'Failed to load conversations'
    } finally {
      historyLoading.value = false
    }
  }

  async function selectConversation(conversation) {
    if (streaming.value) return false

    activeConversationId.value = conversation.id
    conversationId.value = conversation.id
    saveToHistory.value = true
    hydrateMessages(conversation.messages)

    return true
  }

  function startNewConversation({ saveToHistory: save = true } = {}) {
    if (streaming.value) return false

    activeConversationId.value = null
    conversationId.value = createConversationId()
    saveToHistory.value = save
    resetConversationState()

    return true
  }

  async function deleteConversation(conversation) {
    if (streaming.value || isDeletingConversation(conversation.id)) return false

    deletingConversationIds.value = [...deletingConversationIds.value, conversation.id]
    historyError.value = ''

    try {
      const response = await fetch(`/api/v2/conversations/${conversation.id}`, {
        method: 'DELETE',
      })

      if (!response.ok) throw new Error('Failed to delete conversation')

      conversations.value = conversations.value.filter((item) => item.id !== conversation.id)

      if (activeConversationId.value === conversation.id) {
        startNewConversation()
      }

      return true
    } catch (error) {
      historyError.value = error.message || 'Failed to delete conversation'
      return false
    } finally {
      deletingConversationIds.value = deletingConversationIds.value.filter((id) => id !== conversation.id)
    }
  }

  function handleServerEvent(event) {
    if (event.type === 'delta') {
      streamBufferRaw.value += event.text ?? ''
    } else if (event.type === 'done') {
      finishStreaming()
    } else if (event.type === 'error') {
      messages.value.push({ role: 'assistant', content: `⚠️ ${event.message}`, error: true })
      activeTool.value = null
      pendingToolCalls.clear()
      streaming.value = false
      lastFailedSend.value = currentAttempt
    } else if (event.type === 'update_form_field') {
      // This is an out-of-band UI event, not a conversation message.
    } else if (event.type === 'ask_user') {
      // Out-of-band UI event: surface an interactive question to the user.
      pendingQuestion.value = event.data ?? null
    } else if (event.type === 'request_boost') {
      // Out-of-band UI event: surface the Salesforce boost picker to the user.
      openBoostPicker(event.data)
    } else if (isToolCallMessage(event)) {
      applyToolCall(event)
    } else if (isToolResultMessage(event)) {
      applyToolResult(event)
    } else if (event?.role === 'assistant' || (event?.type === 'message' && event?.role === 'assistant')) {
      applyAssistantMessage(event)
    } else if (event?.role === 'user') {
      messages.value.push({
        role: 'user',
        content: normalizeAssistantContent(event.content),
      })
    }

    notifyEvent(event)
  }

  async function sendMessage(content, options = {}) {
    const message = (content ?? '').trim()
    // A regenerate has no new text of its own: it relies on truncateAt alone to
    // tell the server where to resume from.
    if ((!message && !options.truncateAt) || streaming.value) return false

    if (!options.silent) {
      // Interactive prompts send the model a restated answer but show only the choice.
      messages.value.push({ role: 'user', content: options.display ?? message })
    }

    currentAttempt = { content: message, options }
    lastFailedSend.value = null
    streaming.value = true
    streamBufferRaw.value = ''
    activeTool.value = null
    pendingQuestion.value = null
    pendingBoost.value = null
    pendingToolPrompt.value = null

    const controller = new AbortController()
    activeRequestController = controller

    try {
      const body = {}

      if (message) body.message = message
      if (options.truncateAt) body.truncate_at = options.truncateAt

      if (messages.value.length === 1) {
        if (typeof options.system_prompt === 'string' && options.system_prompt.length > 0) {
          body.system_prompt = options.system_prompt
        }

        if (typeof options.system_prompt_args === 'object') {
          body.system_prompt_args = options.system_prompt_args
        }

        if (!saveToHistory.value) {
          body.save_to_history = false
        }
      }

      const response = await fetch(`/api/v2/conversations/${conversationId.value}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      })

      if (!response.ok) throw new Error('Failed to send message')
      if (!response.body) throw new Error('Missing response stream')

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      let done = false

      while (!done) {
        const chunk = await reader.read()
        done = chunk.done

        if (done) {
          break
        }

        const value = chunk.value

        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.replace(/\r\n/g, '\n').split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data:')) continue
          handleServerEvent(JSON.parse(line.slice(5).trim()))
        }
      }

      buffer += decoder.decode()

      if (buffer.startsWith('data:')) {
        handleServerEvent(JSON.parse(buffer.slice(5).trim()))
      }

      if (streaming.value) {
        finishStreaming()
      }

      if (!activeConversationId.value) {
        activeConversationId.value = conversationId.value
      }

      if (saveToHistory.value) {
        await loadConversations()
      }

      return true
    } catch (error) {
      if (error.name === 'AbortError') {
        // User-initiated stop, not a failure: keep whatever streamed so far
        // instead of discarding it, and clear any tool call left dangling.
        cancelPendingToolCalls()
        finishStreaming()
        return false
      }

      messages.value.push({ role: 'assistant', content: `⚠️ ${error.message || 'Failed to send message'}`, error: true })
      activeTool.value = null
      pendingToolCalls.clear()
      streamBufferRaw.value = ''
      streaming.value = false
      lastFailedSend.value = currentAttempt

      return false
    } finally {
      if (activeRequestController === controller) {
        activeRequestController = null
      }
    }
  }

  function popTrailingFailedMessages() {
    // Drop the error bubble and any tool call left dangling mid-flight so a retry
    // doesn't leave a stuck spinner alongside the new attempt.
    while (messages.value.length) {
      const last = messages.value[messages.value.length - 1]
      if (last?.error || (last?.role === 'tool_call' && last.status !== 'done')) {
        messages.value.pop()
        continue
      }
      break
    }
  }

  async function retryLastMessage() {
    if (streaming.value || !lastFailedSend.value) return false

    const { content, options } = lastFailedSend.value
    popTrailingFailedMessages()

    // silent: the user bubble for this content is already on screen from the
    // original attempt, so sendMessage shouldn't push a duplicate.
    return sendMessage(content, { ...options, silent: true })
  }

  // How many prior messages share `role`, i.e. which occurrence (0-indexed) of
  // that role the message at `index` is. Mirrors softDeleteFromOccurrence
  // server-side, which counts the same way over the persisted history.
  function ordinalOf(role, index) {
    return messages.value.slice(0, index).filter((entry) => entry.role === role).length
  }

  // Shared by regenerate and edit: both discard a message and everything after
  // it locally, then have the server do the same (soft-deleting, not losing the
  // history) before resuming. They only differ in which role they target and
  // whether there's new text to go with it.
  async function truncateAndResend(index, role, { text = '', display } = {}) {
    if (streaming.value) return false

    const target = messages.value[index]
    if (!target || target.role !== role) return false

    const ordinal = ordinalOf(role, index)
    messages.value = messages.value.slice(0, index)

    return sendMessage(text, { silent: !text, display, truncateAt: { role, ordinal } })
  }

  // No new text: the server re-runs on the truncated history, which already
  // ends with the user turn this message answered.
  function regenerateMessage(index) {
    return truncateAndResend(index, 'assistant')
  }

  // Aborts the in-flight request; the fetch loop's catch block handles the
  // resulting AbortError and finishes streaming with whatever was received.
  function stopStreaming() {
    activeRequestController?.abort()
  }

  function editUserMessage(index, newContent) {
    const text = (newContent ?? '').trim()
    if (!text) return false

    return truncateAndResend(index, 'user', { text, display: text })
  }

  onBeforeUnmount(() => {
    activeRequestController?.abort()
    activeRequestController = null
    eventHandlers.clear()
  })

  return {
    activeConversationId,
    activeTool,
    conversations,
    deleteConversation,
    editUserMessage,
    historyError,
    historyLoading,
    isDeletingConversation,
    loadConversations,
    messages,
    onEvent,
    openBoostPicker,
    openToolPrompt,
    pendingBoost,
    pendingQuestion,
    pendingToolPrompt,
    regenerateMessage,
    retryLastMessage,
    saveToHistory,
    selectConversation,
    sendMessage,
    startNewConversation,
    stopStreaming,
    streamBuffer,
    streaming,
  }
}
